import { MutationObserver, QueryClient } from "@tanstack/react-query";

import { type AssetMetadata } from "./useUploads";

import { uploadAssetMutationOptions } from "@/api/catalogAssetUploads";
import { skuStoreKeys } from "@/api/products";

export type UploadStatus = "queued" | "uploading" | "done" | "failed";

export type UploadItem = {
  id: string;
  siteId: string;
  file: File | null;
  fileName: string;
  fileSize: number;
  metadata: AssetMetadata;
  status: UploadStatus;
  progress: number;
  errorMessage?: string;
  startedAt?: number;
};

const UPLOADS_QUERY_KEY = ["uploads"] as const;
const MAX_CONCURRENT = 3;

class UploadManager {
  private qc: QueryClient | null = null;
  private active = new Map<string, AbortController>();
  private pending: string[] = [];
  private batchInProgress = false;
  // Set of site IDs touched by the current batch — invalidated on settle.
  // Multiple `addFiles` calls for different siteIds while a batch is in flight
  // all fold into the same batch; each unique site gets its list invalidated.
  private batchSiteIds = new Set<string>();

  init(queryClient: QueryClient) {
    this.qc = queryClient;
    if (!this.qc.getQueryData(UPLOADS_QUERY_KEY)) {
      this.qc.setQueryData<UploadItem[]>(UPLOADS_QUERY_KEY, []);
    }
  }

  getItems(): UploadItem[] {
    if (!this.qc) return [];
    return this.qc.getQueryData<UploadItem[]>(UPLOADS_QUERY_KEY) ?? [];
  }

  hasActiveUploads(): boolean {
    return this.getItems().some(
      (i) => i.status === "queued" || i.status === "uploading",
    );
  }

  addFiles(siteId: string, files: File[], metadata: AssetMetadata): void {
    if (!this.qc || files.length === 0) return;
    const newItems: UploadItem[] = files.map((file) => ({
      id: crypto.randomUUID(),
      siteId,
      file,
      fileName: file.name,
      fileSize: file.size,
      metadata,
      status: "queued",
      progress: 0,
    }));

    this.setItems([...this.getItems(), ...newItems]);

    this.batchInProgress = true;
    this.batchSiteIds.add(siteId);

    for (const item of newItems) {
      this.pending.push(item.id);
    }
    this.tryStart();
  }

  cancel(id: string): void {
    const controller = this.active.get(id);
    if (controller) {
      controller.abort();
      this.active.delete(id);
    }
    this.pending = this.pending.filter((pid) => pid !== id);
    this.setItems(this.getItems().filter((i) => i.id !== id));
    this.checkBatchSettled();
    this.tryStart();
  }

  private setItems(items: UploadItem[]) {
    this.qc?.setQueryData<UploadItem[]>(UPLOADS_QUERY_KEY, items);
  }

  private updateItem(id: string, patch: Partial<UploadItem>) {
    const items = this.getItems();
    const idx = items.findIndex((i) => i.id === id);
    if (idx === -1) return;
    const next = [...items];
    next[idx] = { ...next[idx], ...patch };
    this.setItems(next);
  }

  private tryStart() {
    if (!this.qc) return;
    while (this.active.size < MAX_CONCURRENT && this.pending.length > 0) {
      const id = this.pending.shift()!;
      const item = this.getItems().find((i) => i.id === id);
      if (!item || !item.file) continue;
      const controller = new AbortController();
      this.active.set(id, controller);
      this.updateItem(id, {
        status: "uploading",
        startedAt: Date.now(),
      });
      void this.runOne(item, controller);
    }
  }

  private async runOne(item: UploadItem, controller: AbortController) {
    if (!item.file || !this.qc) return;
    // Run the upload through react-query's mutation system. The manager lives
    // outside React, so it drives the shared mutation via a `MutationObserver`
    // rather than the `useUploadAsset` hook; per-item abort/progress are passed
    // as mutation variables.
    const observer = new MutationObserver(
      this.qc,
      uploadAssetMutationOptions(item.siteId),
    );
    try {
      await observer.mutate({
        file: item.file,
        metadata: item.metadata,
        options: {
          signal: controller.signal,
          onProgress: (percent) => {
            // Drop progress updates if the item was removed mid-flight.
            if (!this.getItems().some((i) => i.id === item.id)) return;
            this.updateItem(item.id, { progress: percent });
          },
        },
      });
      this.updateItem(item.id, {
        status: "done",
        progress: 100,
        file: null,
      });
    } catch (err) {
      // Aborted uploads were already removed from the cache by `cancel()`.
      if (controller.signal.aborted) return;
      this.updateItem(item.id, {
        status: "failed",
        file: null,
        errorMessage: err instanceof Error ? err.message : String(err),
      });
    } finally {
      this.active.delete(item.id);
      this.checkBatchSettled();
      this.tryStart();
    }
  }

  private checkBatchSettled() {
    if (
      this.batchInProgress &&
      this.active.size === 0 &&
      this.pending.length === 0
    ) {
      this.batchInProgress = false;
      const siteIds = Array.from(this.batchSiteIds);
      this.batchSiteIds.clear();
      if (this.qc) {
        // Partial-match prefix for the ProductCatalog asset list,
        // regardless of query params, so whichever Asset Library page/view the
        // user is looking at refetches and shows the new uploads on each site.
        for (const siteId of siteIds) {
          void this.qc.invalidateQueries({
            queryKey: [...skuStoreKeys.all, "catalogAssets", siteId],
          });
        }
      }
    }
  }

  /** Test-only: reset internal state. Do not call in production code. */
  _resetForTests() {
    for (const ctrl of this.active.values()) ctrl.abort();
    this.active.clear();
    this.pending = [];
    this.batchInProgress = false;
    this.batchSiteIds.clear();
    this.setItems([]);
  }
}

export const uploadManager = new UploadManager();
